import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AlertTriangle, Inbox, RefreshCw, Send } from 'lucide-react'
import {
  ErpTable, KpiStrip, MetricCard, Panel, SectionHeader, StatusBadge, Td, Th,
} from '@/components/erp/primitives'
import { ActiveFilters, DateRange, FilterBar, MultiSelect, SearchInput } from '@/shared/ui/filters'
import { CursorPager } from '@/shared/ui/CursorPager'
import { EmptyState, ErrorStrip, Forbidden, LoadingRows } from '@/shared/ui/states'
import { useCursorPage } from '@/shared/hooks/useCursorPage'
import { useDebounced } from '@/shared/hooks/useDebounced'
import { useLoad } from '@/features/administration/hooks'
import { useCan } from '@/shared/auth/session'
import { fmtDateTime, fmtNumber, fmtPercent, humanise } from '@/shared/lib/format'
import { countActive } from '@/shared/lib/query'
import { cn } from '@/lib/utils'
import {
  listNotifications, notificationFacets, notificationSummary, type NotificationFilters,
} from './api'
import { NotificationDetailDrawer } from './NotificationDetailDrawer'
import { SendEmailDialog } from './SendEmailDialog'
import type { NotificationSummaryResponse } from '@/shared/api/types'

/**
 * Notification Log — dedicated page.
 *
 * Same composition as the audit log because they are the same kind of screen:
 * a high-volume append-only stream with a summary above it. Deliberately not a
 * second visual language for what is structurally one pattern.
 */
export function NotificationsPage() {
  const allowed = useCan('notification:read')
  const canSend = useCan('notification:send')
  const [params, setParams] = useSearchParams()
  const [selected, setSelected] = useState<NotificationSummaryResponse | null>(null)
  const [composing, setComposing] = useState(false)

  const filters = useMemo<NotificationFilters>(() => ({
    status: params.getAll('status'),
    channel: params.getAll('channel'),
    type: params.getAll('type'),
    recipient: params.get('recipient') ?? '',
    recipientUserId: params.get('recipientUserId') ?? '',
    q: params.get('q') ?? '',
    fromDay: params.get('from') ?? '',
    toDay: params.get('to') ?? '',
    entityType: params.get('entityType') ?? '',
    entityId: params.get('entityId') ?? '',
    requestId: params.get('requestId') ?? '',
    failedOnly: params.get('failedOnly') === 'true',
  }), [params])

  const [draftQ, setDraftQ] = useState(filters.q)
  const debouncedQ = useDebounced(draftQ, 300)
  useEffect(() => { setDraftQ(filters.q) }, [filters.q])

  const patch = useCallback((next: Partial<NotificationFilters>) => {
    const m = { ...filters, ...next }
    const sp = new URLSearchParams()
    m.status.forEach((v) => sp.append('status', v))
    m.channel.forEach((v) => sp.append('channel', v))
    m.type.forEach((v) => sp.append('type', v))
    if (m.recipient) sp.set('recipient', m.recipient)
    if (m.recipientUserId) sp.set('recipientUserId', m.recipientUserId)
    if (m.q) sp.set('q', m.q)
    if (m.fromDay) sp.set('from', m.fromDay)
    if (m.toDay) sp.set('to', m.toDay)
    if (m.entityType) sp.set('entityType', m.entityType)
    if (m.entityId) sp.set('entityId', m.entityId)
    if (m.requestId) sp.set('requestId', m.requestId)
    if (m.failedOnly) sp.set('failedOnly', 'true')
    setParams(sp, { replace: true })
  }, [filters, setParams])

  useEffect(() => {
    if (debouncedQ !== filters.q) patch({ q: debouncedQ })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ])

  const filterKey = JSON.stringify(filters)
  const list = useCursorPage<NotificationSummaryResponse>(
    (cursor) => listNotifications(filters, cursor, 50),
    [filterKey],
  )
  const summary = useLoad(() => notificationSummary(filters), [filterKey])
  const facets = useLoad(() => notificationFacets(), [])

  const activeCount = countActive({
    status: filters.status, channel: filters.channel, type: filters.type,
    recipient: filters.recipient, recipientUserId: filters.recipientUserId, q: filters.q,
    from: filters.fromDay, to: filters.toDay, entityType: filters.entityType,
    entityId: filters.entityId, requestId: filters.requestId,
    failedOnly: filters.failedOnly,
  })

  const clearAll = () => {
    setDraftQ('')
    setParams(new URLSearchParams(), { replace: true })
  }

  const refreshAll = () => { list.reload(); summary.reload() }

  if (!allowed) return <Forbidden what="the notification log" />

  const s = summary.data
  const f = facets.data
  const facetOpts = (rows?: { value: string; count: number }[]) =>
    (rows ?? []).map((r) => ({ value: r.value, label: humanise(r.value), count: r.count }))

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Notification Log"
        description="Every outbound message the platform has sent, with delivery outcome and the body that went out."
        right={
          <>
            {canSend && (
              <button className="erp-btn erp-btn-primary" onClick={() => setComposing(true)} aria-label="Send email">
                <Send className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Send email</span>
              </button>
            )}
            <button className="erp-btn" onClick={refreshAll} aria-label="Refresh">
              <RefreshCw className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Refresh</span>
            </button>
          </>
        }
      />

      <KpiStrip>
        <MetricCard
          label="Total messages"
          value={s ? fmtNumber(s.total) : '—'}
          sub={activeCount > 0 ? 'matching current filter' : 'all time'}
          accent
        />
        {/* No `delta` here: MetricCard renders a delta as a trend arrow, and the
            success rate is a level, not a change. A red ↓75% beside "75.2%
            success rate" reads as "down 75%", which is not what happened. */}
        <MetricCard
          label="Delivered"
          value={s ? fmtNumber(s.sent) : '—'}
          sub={s ? `${fmtPercent(s.successRate)} success rate` : undefined}
        />
        <MetricCard label="Failed" value={s ? fmtNumber(s.failed) : '—'} sub="needs attention" />
        <MetricCard label="Pending" value={s ? fmtNumber(s.pending) : '—'} sub="queued, not yet sent" />
        <MetricCard label="Today" value={s ? fmtNumber(s.today) : '—'} sub="Africa/Nairobi" />
      </KpiStrip>

      <FilterBar
        activeCount={activeCount} right={<ActiveFilters count={activeCount} onClear={clearAll} />}>
        <SearchInput
          value={draftQ}
          onChange={setDraftQ}
          placeholder="Search recipient or subject…"
        />
        <MultiSelect
          label="Status"
          options={facetOpts(f?.statuses)}
          selected={filters.status}
          onChange={(status) => patch({ status })}
        />
        <MultiSelect
          label="Type"
          options={facetOpts(f?.types)}
          selected={filters.type}
          onChange={(type) => patch({ type })}
        />
        <MultiSelect
          label="Channel"
          options={facetOpts(f?.channels)}
          selected={filters.channel}
          onChange={(channel) => patch({ channel })}
        />
        <DateRange
          from={filters.fromDay}
          to={filters.toDay}
          onChange={({ from, to }) => patch({ fromDay: from, toDay: to })}
        />
        <button
          type="button"
          onClick={() => patch({ failedOnly: !filters.failedOnly })}
          className={cn(
            'h-9 px-3 inline-flex items-center gap-1.5 border rounded-md text-erp-sm axis-full-sm',
            filters.failedOnly
              ? 'border-red-300 bg-red-50 text-red-700 font-medium'
              : 'border-input bg-background text-muted-foreground hover:text-foreground',
          )}
        >
          <AlertTriangle className="h-3.5 w-3.5" /> Failures only
        </button>
      </FilterBar>

      {s && s.topFailures.length > 0 && (
        <Panel
          className="axis-hide-sm"
          title="Top failure reasons"
          description="Grouped on a normalised prefix — provider errors embed message ids"
          dense
        >
          <ul className="space-y-1.5">
            {s.topFailures.slice(0, 5).map((r) => (
              <li key={r.reason} className="flex items-start gap-2 text-erp-sm">
                <AlertTriangle className="h-3.5 w-3.5 text-red-600 mt-0.5 shrink-0" />
                <span className="min-w-0 flex-1 break-words">{r.reason}</span>
                <span className="erp-metric text-erp-xs text-muted-foreground shrink-0">
                  {fmtNumber(r.count)}
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <Panel bodyClassName="p-0">
        <ErrorStrip message={list.error} />
        <ErpTable
          head={
            <>
              <Th>Created</Th>
              <Th>Recipient</Th>
              <Th className="axis-hide-sm">Subject</Th>
              <Th className="axis-hide-sm">Type</Th>
              <Th className="axis-hide-sm">Channel</Th>
              <Th>Status</Th>
              <Th className="axis-hide-sm">Attempts</Th>
              <Th className="axis-hide-sm">Sent</Th>
            </>
          }
        >
          {list.loading && list.rows.length === 0 ? (
            <LoadingRows colSpan={8} />
          ) : list.rows.length === 0 ? (
            <tr>
              <td colSpan={8}>
                <EmptyState
                  icon={<Inbox className="h-4 w-4" />}
                  title={activeCount > 0 ? 'No messages match this filter' : 'No messages sent yet'}
                  hint={activeCount > 0
                    ? 'Widen the date range or clear a filter.'
                    : 'Invitations, password resets and security alerts all land here.'}
                  action={activeCount > 0
                    ? <button className="erp-btn" onClick={clearAll}>Clear filters</button>
                    : undefined}
                />
              </td>
            </tr>
          ) : (
            list.rows.map((m) => (
              <tr
                key={m.id}
                onClick={() => setSelected(m)}
                className="hover:bg-muted/40 cursor-pointer"
                tabIndex={0}
                onKeyDown={(ev) => { if (ev.key === 'Enter') setSelected(m) }}
              >
                <Td className="whitespace-nowrap erp-metric">{fmtDateTime(m.createdAt)}</Td>
                <Td className="truncate max-w-[16rem]">{m.recipient}</Td>
                <Td className="axis-hide-sm truncate max-w-[22rem]">{m.subject}</Td>
                <Td className="axis-hide-sm">{humanise(m.type)}</Td>
                <Td className="axis-hide-sm">{humanise(m.channel)}</Td>
                <Td>
                  <div className="flex items-center gap-1.5">
                    <StatusBadge status={m.status} />
                    {m.status === 'FAILED' && m.error && (
                      <span className="axis-hide-sm text-erp-xs text-red-700 truncate max-w-[12rem]">
                        {m.error}
                      </span>
                    )}
                  </div>
                </Td>
                <Td className="axis-hide-sm erp-metric">{m.attempts}</Td>
                <Td className="axis-hide-sm whitespace-nowrap erp-metric">{fmtDateTime(m.sentAt)}</Td>
              </tr>
            ))
          )}
        </ErpTable>

        <CursorPager
          page={list.page}
          count={list.rows.length}
          hasMore={list.hasMore}
          canGoBack={list.canGoBack}
          onBack={list.back}
          onNext={list.next}
          loading={list.loading}
          unit="messages"
        />
      </Panel>

      <NotificationDetailDrawer
        message={selected}
        onClose={() => setSelected(null)}
        onChanged={refreshAll}
      />
      <SendEmailDialog
        open={composing}
        onClose={() => setComposing(false)}
        onSent={refreshAll}
      />
    </div>
  )
}
